import { safeParseFloat } from "encamp-shared/src/utils/safeParseNumber";
import {
  EpisodicEventManifestsQuery,
  EpisodicManifestCandidatesQuery,
  EpisodicReliefUnavailableReason,
  EpisodicStreamExemptionInput,
} from "generated-graphql/graphql";

import { episodicWindowEnd, formatPounds } from "./episodicDisplay";

export type EventManifestExemptions = NonNullable<
  EpisodicEventManifestsQuery["episodicEvent"]
>["manifestExemptions"];
export type ExemptManifest = EventManifestExemptions["manifests"][number];
export type ExemptStream = ExemptManifest["streams"][number];
export type ManifestCandidate = NonNullable<
  EpisodicManifestCandidatesQuery["episodicEvent"]
>["manifestCandidates"][number];

/**
 * Whether declared pounds will leave generator status. The server exempts them
 * everywhere but a state that has not adopted subpart L; an LQG facility's relief
 * is "likely not required", not withheld, so its declarations still apply.
 */
export function reliefAppliesToDeclarations(relief: {
  reasonKind?: EpisodicReliefUnavailableReason | null;
}): boolean {
  return relief.reasonKind !== EpisodicReliefUnavailableReason.StateNotAdopted;
}

export function streamCapPounds(stream: {
  groupPounds: number;
  exemptPoundsOnOtherEvents: number;
}): number {
  return Math.max(0, stream.groupPounds - stream.exemptPoundsOnOtherEvents);
}

export const MANIFEST_REFETCH_QUERIES = ["EpisodicEventManifests", "FacilityEpisodicEvents"];

export type StreamAmountDraft = {
  wasteStreamId: string;
  name: string;
  groupPounds: number;
  capPounds: number;
  matchingWasteCodes: string[];
  /** The stream's first shipment on this manifest, which the server judges the window on. */
  firstShipped: string;
  /** False when that shipment falls outside the event's window, so the stream cannot be declared. */
  inWindow: boolean;
  /** Pounds this event already holds on the stream, or null; a locked stream's are removed on save. */
  declaredPounds: number | null;
  /** Explicit pounds, "0" until the user types; only a non-zero amount is declared. */
  pounds: string;
};

/**
 * A stream the user cannot declare on: shipped outside the window, or held entirely by other
 * events. It declares nothing, so saving the manifest drops any pounds this event had on it.
 */
export function isLockedDraft(draft: Pick<StreamAmountDraft, "inWindow" | "capPounds">): boolean {
  return !draft.inWindow || draft.capPounds <= 0;
}

/** Whether a shipment date (YYYY-MM-DD) falls inside the event's 60-day window, deadline day included. */
export function shippedInWindow(shippedDate: string, startDate: string): boolean {
  const windowEnd = episodicWindowEnd(startDate);
  return windowEnd !== null && shippedDate >= startDate && shippedDate <= windowEnd;
}

export function draftsForManifest(
  candidate: ManifestCandidate,
  existing: ExemptManifest | null,
  startDate: string
): StreamAmountDraft[] {
  return candidate.streams.map((stream) => {
    const declared =
      existing?.streams.find((s) => s.wasteStream.id === stream.wasteStream.id)?.exemption ?? null;
    const capPounds = streamCapPounds(stream);
    const inWindow = shippedInWindow(stream.firstShipped, startDate);
    const declaredPounds = declared?.declaredPounds ?? null;
    return {
      wasteStreamId: stream.wasteStream.id,
      name: stream.wasteStream.name,
      groupPounds: stream.groupPounds,
      capPounds,
      matchingWasteCodes: stream.matchingWasteCodes,
      firstShipped: stream.firstShipped,
      inWindow,
      declaredPounds,
      pounds:
        declaredPounds === null || isLockedDraft({ inWindow, capPounds })
          ? "0"
          : String(declaredPounds),
    };
  });
}

const toHundredths = (pounds: number) => Math.round(pounds * 100) / 100;

/** Over the cap as displayed: the row shows the cap to the hundredth, so that hundredth is allowed. */
function overCap(pounds: number, draft: StreamAmountDraft): boolean {
  return toHundredths(pounds) > toHundredths(draft.capPounds);
}

/** The typed amount when it is a usable number within the cap; 0, blank and a locked stream are "nothing declared". */
function parsedPounds(draft: StreamAmountDraft): number | null {
  if (isLockedDraft(draft)) {
    return 0;
  }
  const pounds = draft.pounds.trim() === "" ? 0 : safeParseFloat(draft.pounds);
  if (pounds == null || pounds < 0 || overCap(pounds, draft)) {
    return null;
  }
  return pounds;
}

/** The pounds this draft declares, or null when it declares nothing or is invalid. */
export function draftPounds(draft: StreamAmountDraft): number | null {
  const pounds = parsedPounds(draft);
  return pounds === null || pounds === 0 ? null : pounds;
}

export function draftError(draft: StreamAmountDraft): string | null {
  if (isLockedDraft(draft)) {
    return null;
  }
  const pounds = draft.pounds.trim() === "" ? 0 : safeParseFloat(draft.pounds);
  if (pounds == null || pounds < 0) {
    return "Enter 0 lb or more";
  }
  if (overCap(pounds, draft)) {
    if (draft.capPounds <= 0) {
      return `Other events already hold all of ${draft.name} on this manifest`;
    }
    return draft.capPounds < draft.groupPounds
      ? `${formatPounds(draft.capPounds)} of ${draft.name} left after other events`
      : `Only ${formatPounds(draft.capPounds)} of ${draft.name} is on this manifest`;
  }
  return null;
}

/** Every row is a valid amount and at least one is above zero. */
export function canSaveDrafts(drafts: StreamAmountDraft[]): boolean {
  return (
    drafts.every((draft) => parsedPounds(draft) !== null) &&
    drafts.some((draft) => draftPounds(draft) !== null)
  );
}

export function draftsToInputs(drafts: StreamAmountDraft[]): EpisodicStreamExemptionInput[] {
  return drafts.flatMap((draft) => {
    const pounds = draftPounds(draft);
    return pounds == null ? [] : [{ wasteStreamId: draft.wasteStreamId, pounds }];
  });
}

export function runningTotal(
  currentTotal: number,
  existing: ExemptManifest | null,
  drafts: StreamAmountDraft[],
  reliefApplies = true
): { before: number; after: number } {
  // Where relief does not reach the facility the server stores the rows but
  // exempts nothing, so the projection must not promise an increase.
  if (!reliefApplies || !canSaveDrafts(drafts)) {
    return { before: currentTotal, after: currentTotal };
  }
  const draftSum = drafts.reduce((sum, draft) => sum + (draftPounds(draft) ?? 0), 0);
  return { before: currentTotal, after: currentTotal - (existing?.exemptPounds ?? 0) + draftSum };
}
